/**
 * EVUS新注册 API服务
 *
 * 提供与后端API通信的方法
 */

import { getApiBaseUrl } from '../utils/apiConfig.js';

const API_BASE_URL = getApiBaseUrl();
const EVUS_ENROLL_API = `${API_BASE_URL}/evus/enroll`;

/**
 * 启动EVUS新注册任务
 *
 * @param {Object} formData 表单数据
 * @returns {Promise} 包含task_id的响应
 */
export async function startEvusEnroll(formData) {
  const resp = await fetch(`${EVUS_ENROLL_API}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(formData),
  });

  // 先获取响应文本，检查格式
  const text = await resp.text();
  let data;

  try {
    data = JSON.parse(text);
  } catch (e) {
    // 如果不是JSON格式，说明服务器可能返回了错误页面或其他格式
    console.error('服务器响应格式错误，响应内容:', text.substring(0, 200));
    throw new Error(`服务器响应格式错误，请检查网络连接或稍后重试`);
  }

  if (!resp.ok) {
    // 处理特殊的402积分不足状态
    if (resp.status === 402) {
      throw new Error('积分不足,请先充值');
    }
    throw new Error(data?.detail || '提交失败');
  }

  return data;
}

/**
 * 获取EVUS新注册任务状态
 *
 * @param {string} taskId 任务ID
 * @returns {Promise} 包含状态信息的响应
 */
export async function getEvusEnrollStatus(taskId) {
  try {
    const resp = await fetch(`${EVUS_ENROLL_API}/status/${taskId}`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (resp.status === 404) {
      throw new Error('任务不存在');
    }

    const data = await resp.json();

    if (!resp.ok) {
      throw new Error(data?.detail || '获取状态失败');
    }

    return data;
  } catch (err) {
    // 网络错误时返回null,让组件继续轮询
    console.error('状态轮询出错:', err);
    return null;
  }
}

/**
 * 获取完整的注册结果
 *
 * @param {string} taskId 任务ID
 * @returns {Promise} 包含enrollment_number和screenshot的结果
 */
export async function getEvusEnrollResult(taskId) {
  const resp = await fetch(`${EVUS_ENROLL_API}/result/${taskId}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.detail || '获取结果失败');
  }

  return data;
}

/**
 * 取消EVUS新注册任务
 *
 * @param {string} taskId 任务ID
 * @returns {Promise} 确认消息
 */
export async function cancelEvusEnroll(taskId) {
  const resp = await fetch(`${EVUS_ENROLL_API}/${taskId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data?.detail || '取消任务失败');
  }

  return data;
}

/**
 * 启动EVUS新注册任务 - 优化版（用户友好的简化表单）
 *
 * 优化特性：
 * - 用户只填一次个人信息
 * - 系统智能复用数据到美国联系人和紧急联系人
 * - 后端完整映射为标准EVUS格式（所有字段完整无缺）
 *
 * @param {Object} optimizedFormData 优化版表单数据 (EvusEnrollRequestOptimized)
 * @returns {Promise} 包含task_id的响应
 */
export async function startEvusEnrollOptimized(optimizedFormData) {
  const resp = await fetch(`${EVUS_ENROLL_API}/optimized/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(optimizedFormData),
  });

  // 先获取响应文本，检查格式
  const text = await resp.text();
  let data;

  try {
    data = JSON.parse(text);
  } catch (e) {
    console.error('服务器响应格式错误，响应内容:', text.substring(0, 200));
    throw new Error(`服务器响应格式错误，请检查网络连接或稍后重试`);
  }

  if (!resp.ok) {
    // 处理特殊的402积分不足状态
    if (resp.status === 402) {
      throw new Error('积分不足,请先充值');
    }
    throw new Error(data?.detail || '提交失败');
  }

  return data;
}
